// src/components/Enrollments/hooks/useEnrollmentsController.js
// ============================================================================
// BLOCK ENR.HOOK.0 — Enrollments page controller
// ----------------------------------------------------------------------------
// PURPOSE
//  - Hold all view state in one place.
//  - Keep the page component focused on rendering only.
// ============================================================================

import React from 'react';

import { exportEnrollmentsCsv, fetchEnrollments } from '../api/enrollmentsApi.js';
import {
  DEFAULT_PAGE_LENGTH,
  DEFAULT_YEAR,
  MAX_YEAR,
  MIN_YEAR,
} from '../constants/enrollmentsUi.js';

function normalizeYearInput(rawValue) {
  if (rawValue === '') {
    return '';
  }

  const parsed = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_YEAR;
  }

  return Math.min(Math.max(parsed, MIN_YEAR), MAX_YEAR);
}

export default function useEnrollmentsController() {
  const [selectedRep, setSelectedRep] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState(DEFAULT_YEAR);
  const [customerSearch, setCustomerSearch] = React.useState('');
  const [repSearch, setRepSearch] = React.useState('');
  const [productTypeSearch, setProductTypeSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [totals, setTotals] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_LENGTH);

  const loadData = React.useCallback(async () => {
    if (!selectedRep || selectedYear === '') {
      setRows([]);
      setTotals({});
      setTotalItems(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await fetchEnrollments({
        selectedRep,
        selectedYear,
        page,
        rowsPerPage,
        customerSearch,
        repSearch,
        productTypeSearch,
      });

      setRows(data.enrollments || []);
      setTotals(data.totals || {});
      setTotalItems(data.total || 0);
      setErrorMessage(null);
    } catch (err) {
      setRows([]);
      setTotals({});
      setTotalItems(0);
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to load enrollments.');
    } finally {
      setLoading(false);
    }
  }, [customerSearch, page, productTypeSearch, repSearch, rowsPerPage, selectedRep, selectedYear]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRepChange = React.useCallback((newRep) => {
    setSelectedRep(newRep);
    setPage(0);
    setRowsPerPage(DEFAULT_PAGE_LENGTH);
  }, []);

  const handleYearChange = React.useCallback((event) => {
    setSelectedYear(normalizeYearInput(event.target.value));
    setPage(0);
  }, []);

  const handleChangePage = React.useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handleCustomerSearchChange = React.useCallback((event) => {
    setCustomerSearch(event.target.value);
    setPage(0);
  }, []);

  const handleRepSearchChange = React.useCallback((event) => {
    setRepSearch(event.target.value);
    setPage(0);
  }, []);

  const handleProductTypeSearchChange = React.useCallback((event) => {
    setProductTypeSearch(event.target.value);
    setPage(0);
  }, []);

  const handleExport = React.useCallback(async () => {
    if (!selectedRep || selectedYear === '') {
      return;
    }

    try {
      setErrorMessage(null);
      await exportEnrollmentsCsv({
        selectedRep,
        selectedYear,
        customerSearch,
        repSearch,
        productTypeSearch,
      });
    } catch (err) {
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to export enrollments.');
    }
  }, [customerSearch, productTypeSearch, repSearch, selectedRep, selectedYear]);

  return {
    customerSearch,
    errorMessage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCustomerSearchChange,
    handleExport,
    handleRepChange,
    handleRepSearchChange,
    handleProductTypeSearchChange,
    handleYearChange,
    loading,
    page,
    productTypeSearch,
    repSearch,
    rows,
    rowsPerPage,
    selectedRep,
    selectedYear,
    setErrorMessage,
    totalItems,
    totals,
  };
}
